export const ALL_VALUE = 'all';

export const getProductImageList = (product) => {
  const seen = new Set();
  return [product?.image_url, ...(product?.images || [])]
    .map((value) => (value || '').trim())
    .filter((value) => {
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

export const getLocalizedValue = (value, lang) => {
  if (typeof value === 'object' && value !== null) {
    return value[lang] || value.en || value.ar || '';
  }
  return value || '';
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const getBrandKey = (product) => getLocalizedValue(product?.category, 'en').trim();

const cleanModelName = (value, brand = '') => {
  let model = (value || '')
    .replace(/\bAndroid\s+Screen\b/gi, '')
    .replace(/\b\d{4}\s*-\s*(?:\d{4}|present)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (brand) {
    model = model.replace(new RegExp(`^${escapeRegExp(brand)}\\s+`, 'i'), '').trim();
  }

  return model;
};

export const getVehicleModel = (product) => {
  const brand = getBrandKey(product);
  const compatibleSpec = (product?.specs || [])
    .map((spec) => getLocalizedValue(spec, 'en'))
    .find((spec) => /^compatible with/i.test(spec));

  if (compatibleSpec) {
    return cleanModelName(compatibleSpec.replace(/^compatible with\s+/i, ''), brand);
  }

  return cleanModelName(getLocalizedValue(product?.name, 'en'), brand);
};

export const getVehicleYear = (product) => {
  const yearSpec = (product?.specs || [])
    .map((spec) => getLocalizedValue(spec, 'en'))
    .find((spec) => /^years:/i.test(spec));
  const specYear = yearSpec?.replace(/^years:\s*/i, '').trim();
  if (specYear) return specYear;

  const name = getLocalizedValue(product?.name, 'en');
  return name.match(/\b\d{4}\s*-\s*(?:\d{4}|present)\b/i)?.[0] || '';
};

export const normalizeSearch = (value) => (value || '').toString().toLowerCase().replace(/\s+/g, ' ').trim();

export const getSearchableText = (product, lang) => [
  getLocalizedValue(product?.name, lang),
  getLocalizedValue(product?.name, 'en'),
  getLocalizedValue(product?.category, lang),
  getLocalizedValue(product?.category, 'en'),
  getVehicleModel(product),
  getVehicleYear(product),
  ...(product?.specs || []).map((spec) => getLocalizedValue(spec, lang)),
  ...(product?.specs || []).map((spec) => getLocalizedValue(spec, 'en')),
].join(' ');

export const buildBrandOptions = (products, lang) => {
  const options = new Map();
  products.forEach((product) => {
    const key = getBrandKey(product);
    if (!key) return;
    const label = getLocalizedValue(product.category, lang) || key;
    if (!options.has(key)) options.set(key, label);
  });

  return [...options.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const buildModelOptions = (products, selectedCategory = ALL_VALUE) => {
  const options = new Set();
  products.forEach((product) => {
    if (selectedCategory !== ALL_VALUE && getBrandKey(product) !== selectedCategory) return;
    const model = getVehicleModel(product);
    if (model) options.add(model);
  });
  return [...options].sort((a, b) => a.localeCompare(b));
};

export const buildYearOptions = (products, selectedCategory = ALL_VALUE, selectedModel = ALL_VALUE) => {
  const options = new Set();
  products.forEach((product) => {
    if (selectedCategory !== ALL_VALUE && getBrandKey(product) !== selectedCategory) return;
    if (selectedModel !== ALL_VALUE && getVehicleModel(product) !== selectedModel) return;
    const year = getVehicleYear(product);
    if (year) options.add(year);
  });
  return [...options].sort((a, b) => b.localeCompare(a));
};

export const buildShopFilterUrl = ({ brand, model, year, search, sort, page } = {}) => {
  const params = new URLSearchParams();
  if (brand && brand !== ALL_VALUE) params.set('brand', brand);
  if (model && model !== ALL_VALUE) params.set('model', model);
  if (year && year !== ALL_VALUE) params.set('year', year);
  if (search) params.set('search', search);
  if (sort && sort !== 'default') params.set('sort', sort);
  if (page && Number(page) > 1) params.set('page', String(page));

  const query = params.toString();
  return query ? `/shop?${query}` : '/shop';
};
